import { FC, ComponentProps, useCallback, useContext, useEffect } from 'react';

import { getStaticPrefix } from '@eneco/common/env';
import { ChatContext, useChat } from '@eneco/components/chat/useChat';
import useDC from '@eneco/dc/useDC';
import { I18nProvider, useTranslation } from '@eneco/i18n';
import { Skeleton } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';

import { errorTypes } from '../../../../state/MeterReadingsFlow.const';
import { useMeterReadingsFlow } from '../../../../state/MeterReadingsFlow.machine';
import { Error, ERROR_PAGE_STATES } from '../../../../state/MeterReadingsFlow.types';
import { Simple } from '../../../templates/Simple';

type SimpleProps = ComponentProps<typeof Simple>;

const SimpleIncludingChat: FC<SimpleProps> = props => {
  const { t } = useTranslation();
  const [mount] = useChat();
  const startChat = () => {
    mount({ visibility: 'open', topic: t('chatTopicText') });
  };

  return <Simple {...props} textLinkAction={startChat} />;
};

export const ErrorDefault: FC = () => {
  const { label } = useDC();

  return (
    <I18nProvider dictionary={locale => import(`./content/ErrorDefault.${label}.${locale}.json`)}>
      <ErrorDefaultComponent />
    </I18nProvider>
  );
};
export const ErrorDefaultComponent: FC = () => {
  const { label } = useDC();
  const { t, isLoading } = useTranslation();
  const { trackError } = useTracking();
  const { state } = useMeterReadingsFlow();
  const { errors, reading } = state.context;
  const hasChat = !!useContext(ChatContext);

  const stateValue = (state.value as ERROR_PAGE_STATES).ERROR_PAGE;

  const getErrorMessage = useCallback(
    (error: Error) => {
      const context = error.errorContext?.productType ? ` - ${error.errorContext?.productType}` : '';
      return `Error - Meterreadings - ${
        errorTypes[error.errorType as keyof typeof errorTypes]
      }${context} - ${reading?.readingType}`;
    },
    [reading?.readingType],
  );

  useEffect(() => {
    errors?.forEach((error: Error) => {
      trackError({ message: getErrorMessage(error) });
    });
  }, [errors, trackError, getErrorMessage]);

  if (isLoading) return <Skeleton height={450} />;

  const content = t(`content.${stateValue || 'Standard'}`) || t('content.Standard');

  const props: SimpleProps = {
    title: t('title'),
    content: content,
    textLinkText: t('textLinkText'),
    heroImage: {
      value: {
        src: t('imageSrc') ? `${getStaticPrefix()}/images/${label}/meterreadings/${t('imageSrc')}` : '',
      },
    },
  };

  if (!hasChat) return <Simple {...props} />;

  return <SimpleIncludingChat {...props} />;
};
